/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	changeRowsPerPage,
	onOrderChange,
	setOrderBy,
	setOrder,
	setSelected,
} from '@/features/table/tasksTableSlice';
import { TableRefresh } from '@/components/table/TableRefresh';
import { taskSearch } from '@/utils/typesense/clients/taskSearch';
import { Configure, InstantSearch } from 'react-instantsearch';
import { paths } from '@/app/routes';
import TasksTableFilters from './TasksTableFilters';
import { useInstantSearchOwnerFilters } from '@/hooks/useInstantSearchOwnerFilters';
import InstantSearchDataGrid from '../InstantSearchDataGrid';
import { parseHeadCells } from '../InstantSearchDataGrid/data';
import { TasksBulkActionsToolbar } from '@/components/table/TasksBulkActionsToolbar';

export const taskHeadCells = [
	{
		id: 'status',
		numeric: false,
		label: ' ',
		align: 'left',
		sortable: false,
		hideable: false,
		reorderable: false,
	},
	{
		id: 'subject',
		numeric: false,
		label: 'Task name',
		align: 'left',
		sortable: true,
		hideable: false,
	},
	{ id: 'ownerDocId', numeric: false, label: 'Task assignee', align: 'right', sortable: true },
	{ id: 'deal', numeric: false, label: 'Client', align: 'left', sortable: false },
	{ id: 'contactName', numeric: false, label: 'Contact name', align: 'left', sortable: false },
	{
		id: 'clientOwnerDocId',
		numeric: false,
		label: 'Client owner',
		align: 'left',
		sortable: true,
	},
	{ id: 'createdBy', numeric: false, label: 'Created by', align: 'left', sortable: false },
	{ id: 'taskType', numeric: false, label: 'Task type', align: 'left', sortable: true },
	{ id: 'category', numeric: false, label: 'Category', align: 'left', sortable: true },
	{ id: 'objectType', numeric: false, label: 'Type', align: 'left', sortable: true },
	{ id: 'priority', numeric: false, label: 'Priority', align: 'left', sortable: true },
	{ id: 'lastUpdated', numeric: false, label: 'Last modified', align: 'right', sortable: true },
	{ id: 'dueDate', numeric: false, label: 'Due date', align: 'right', sortable: true },
];

const TasksTable = ({ tab }) => {
	const dispatch = useDispatch();
	const slug = paths.tasks;

	const { rowsPerPage, orders } = useSelector((state) => state.taskTable);
	const orderParams = useMemo(
		() => orders[slug] ?? { order: 'desc', orderBy: 'dueDate' },
		[orders],
	);
	const { order, orderBy } = orderParams;
	const {
		filters,
		filterSchema: filterSchemaObj,
		owners,
		ownerFilter,
	} = useInstantSearchOwnerFilters(slug);

	useEffect(() => {
		dispatch(setSelected([]));
	}, [dispatch, tab]);

	useEffect(() => {
		dispatch(
			setOrderBy({
				slug,
				orderBy: orderBy ?? 'dueDate',
			}),
		);
		dispatch(
			setOrder({
				slug,
				order: order ?? 'desc',
			}),
		);
	}, [slug]);

	useEffect(() => {
		dispatch(onOrderChange());
	}, [orderBy, order]);

	const handleChangeRowsPerPage = (event) => {
		dispatch(changeRowsPerPage(event.target.value));
	};

	const filterSchema = useMemo(() => {
		const tabFilter = `completed := ${tab === 'completed'}`;
		const clientOwnerFilter =
			(filters.clientOwners ?? []).length > 0
				? ` && clientOwnerDocId: [${filters.clientOwners.join(',')}]`
				: '';

		const filterSchemaArray = Object.values(filterSchemaObj).filter(Boolean);
		const schemaString = [`${ownerFilter} && ${tabFilter}${clientOwnerFilter}`]
			.concat(filterSchemaArray)
			.join(' && ');

		const schema = {
			filters: schemaString,
		};

		return schema;
	}, [tab, owners, filters.clientOwners, filterSchemaObj]);

	const columns = useMemo(() => parseHeadCells(taskHeadCells), []);

	return (
		<InstantSearch indexName='tasks' searchClient={taskSearch}>
			<Configure {...filterSchema} />
			<TableRefresh table='taskTable' />
			<TasksTableFilters tab={tab} />
			<TasksBulkActionsToolbar />
			<InstantSearchDataGrid
				rowsPerPage={rowsPerPage}
				onRowsPerPageChange={handleChangeRowsPerPage}
				objectType={'tasks'}
				columns={columns}
				tab={tab}
				checkboxSelection
				enableColumnReorder
			/>
		</InstantSearch>
	);
};

export default TasksTable;
